import apiClient from './client';
import type { TodoComment } from '../types';

// =============================================
// TypeScript Interfaces
// =============================================

export type TodoPriority = 'high' | 'medium' | 'low';

export interface TodoItem {
  id: string;
  todo_list_id: string;
  title: string;
  description?: string | null;
  is_completed: boolean;
  priority: TodoPriority;
  due_date?: string | null;
  assigned_to?: string | null;
  assigned_to_name?: string | null;
  created_by?: string;
  created_by_name?: string | null;
  completed_by?: string | null;
  completed_by_name?: string | null;
  completed_at?: string | null;
  position?: number;
  comment_count?: number;
  created_at: string;
  updated_at: string;
}

// Raw response from backend
interface TodoListRaw {
  id: string;
  name: string;
  icon?: string;
  color?: string;
  household_id?: string | null;
  user_id: string;
  items?: TodoItem[];
  total_items: number;
  completed_items: number;
  pending_items: number;
  created_at: string;
  updated_at: string;
}

export interface TodoList {
  id: string;
  name: string;
  icon?: string;
  color?: string;
  is_household: boolean;
  household_id?: string;
  owner_id: string;
  items: TodoItem[];
  open_count: number;
  completed_count: number;
  total_count: number;
  created_at: string;
  updated_at: string;
}

function mapTodoList(raw: TodoListRaw): TodoList {
  return {
    id: raw.id,
    name: raw.name,
    icon: raw.icon,
    color: raw.color,
    is_household: !!raw.household_id,
    household_id: raw.household_id || undefined,
    owner_id: raw.user_id,
    items: raw.items || [],
    open_count: raw.pending_items,
    completed_count: raw.completed_items,
    total_count: raw.total_items,
    created_at: raw.created_at,
    updated_at: raw.updated_at,
  };
}

export interface TodoListCreate {
  name: string;
  icon?: string;
  color?: string;
  household_id?: string;
}

export interface TodoListUpdate {
  name?: string;
  icon?: string;
  color?: string;
}

export interface TodoItemCreate {
  title: string;
  description?: string;
  priority?: TodoPriority;
  due_date?: string;
  assigned_to?: string;
}

export interface TodoItemUpdate {
  title?: string;
  description?: string;
  priority?: TodoPriority;
  due_date?: string | null;
  assigned_to?: string | null;
  is_completed?: boolean;
}

export interface TodoSummary {
  personal_open: number;
  household_open: number;
  upcoming: TodoItem[];
}

// =============================================
// API Methods
// =============================================

export const todosApi = {
  /**
   * GET /todos — Alle Listen (persoenlich + haushalt)
   */
  list: async (): Promise<TodoList[]> => {
    const response = await apiClient.get<TodoListRaw[]>('/todos');
    return response.data.map(mapTodoList);
  },

  /**
   * GET /todos/{id} — Liste mit Items
   */
  get: async (id: string): Promise<TodoList> => {
    const response = await apiClient.get<TodoListRaw>(`/todos/${id}`);
    return mapTodoList(response.data);
  },

  /**
   * POST /todos — Neue Liste erstellen
   */
  create: async (data: TodoListCreate): Promise<TodoList> => {
    const response = await apiClient.post<TodoListRaw>('/todos', data);
    return mapTodoList(response.data);
  },

  /**
   * PUT /todos/{id} — Liste bearbeiten
   */
  update: async (id: string, data: TodoListUpdate): Promise<TodoList> => {
    const response = await apiClient.put<TodoListRaw>(`/todos/${id}`, data);
    return mapTodoList(response.data);
  },

  /**
   * DELETE /todos/{id} — Liste loeschen
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/todos/${id}`);
  },

  /**
   * POST /todos/{listId}/items — Item hinzufuegen
   */
  addItem: async (listId: string, data: TodoItemCreate): Promise<TodoItem> => {
    const response = await apiClient.post<TodoItem>(`/todos/${listId}/items`, data);
    return response.data;
  },

  /**
   * PUT /todos/{listId}/items/{itemId} — Item bearbeiten
   */
  updateItem: async (
    listId: string,
    itemId: string,
    data: TodoItemUpdate,
  ): Promise<TodoItem> => {
    const response = await apiClient.put<TodoItem>(
      `/todos/${listId}/items/${itemId}`,
      data,
    );
    return response.data;
  },

  /**
   * DELETE /todos/{listId}/items/{itemId} — Item loeschen
   */
  deleteItem: async (listId: string, itemId: string): Promise<void> => {
    await apiClient.delete(`/todos/${listId}/items/${itemId}`);
  },

  /**
   * POST /todos/{listId}/items/{itemId}/toggle — Schnell-Toggle
   */
  toggleItem: async (listId: string, itemId: string): Promise<TodoItem> => {
    const response = await apiClient.post<TodoItem>(
      `/todos/${listId}/items/${itemId}/toggle`,
    );
    return response.data;
  },

  /**
   * POST /todos/{listId}/clear-completed — Erledigte entfernen
   */
  clearCompleted: async (listId: string): Promise<TodoList> => {
    const response = await apiClient.post<TodoListRaw>(`/todos/${listId}/clear-completed`);
    return mapTodoList(response.data);
  },

  /**
   * GET /todos/summary — Offene Aufgaben-Zusammenfassung fuer Dashboard
   */
  summary: async (): Promise<TodoSummary> => {
    const response = await apiClient.get<TodoSummary>('/todos/summary');
    return response.data;
  },

  // =============================================
  // Comments
  // =============================================

  /**
   * GET /todos/{listId}/items/{itemId}/comments — Kommentare laden
   */
  getComments: async (listId: string, itemId: string): Promise<TodoComment[]> => {
    const response = await apiClient.get<TodoComment[]>(
      `/todos/${listId}/items/${itemId}/comments`,
    );
    return response.data;
  },

  /**
   * POST /todos/{listId}/items/{itemId}/comments — Kommentar hinzufuegen
   */
  addComment: async (
    listId: string,
    itemId: string,
    text: string,
  ): Promise<TodoComment> => {
    const response = await apiClient.post<TodoComment>(
      `/todos/${listId}/items/${itemId}/comments`,
      { text },
    );
    return response.data;
  },

  /**
   * DELETE /todos/{listId}/items/{itemId}/comments/{commentId} — Kommentar loeschen
   */
  deleteComment: async (
    listId: string,
    itemId: string,
    commentId: string,
  ): Promise<void> => {
    await apiClient.delete(
      `/todos/${listId}/items/${itemId}/comments/${commentId}`,
    );
  },
};
